import { Admin, AdminLocationEmails } from '../../../../clients/quipli-service/admin/admin';
import { Typography } from '../../../../components/typography/typography';
import { toast } from '../../../../util/toasts';
import { useCallback, useEffect, useState } from 'react';
import { Toggle } from '../../../../components/form/toggle';
import Icon from '../../../../components/icons/icon';
import classNames from 'classnames';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { debounce } from 'lodash';
import { Location } from '../../../../clients/quipli-service/location/location';
import { EmailGroup, EmailItem, emailsGroup, emailsKeys } from '../../config/emails';
import { SelectForm } from '../../../../components/select-form/select-form';
import { components } from 'react-select';
import { useAdminApiV1 } from '../../../../clients/quipli-service/admin/admin-api-v1';

export const AdminUserEmailNotification = ({ adminUser }: { adminUser: Admin }) => {
  const { updateUserAdminEmailNotifications, getUserAdminEmailNotifications, listAdminLocations } = useAdminApiV1();

  const [locations, setLocations] = useState<Location[]>([]),
    [adminEmails, setAdminEmails] = useState<AdminLocationEmails>({}),
    [openGroup, setOpenGroup] = useState<number | null>(null);

  const updateEmailListByLocation = (email: string, locationId: number, isSelected: boolean) => {
    if (isSelected) {
      setAdminEmails((prevState) => {
        if (prevState[locationId]) {
          if (!prevState[locationId].includes(email)) {
            prevState[locationId].push(email);
          }
        } else {
          prevState[locationId] = [email];
        }
        saveAdminEmailsNotification(prevState);
        return prevState;
      });
    } else {
      if (adminEmails[locationId]) {
        setAdminEmails((prevState: AdminLocationEmails) => {
          const locationFilter: string[] = prevState[locationId].filter((item) => item !== email);
          const newState: AdminLocationEmails = prevState;
          newState[locationId] = locationFilter;
          saveAdminEmailsNotification(newState);
          return newState;
        });
      }
    }
  };

  const selectAllPermissionsByLocation = async (locationId: number) => {
    if (!isLocationSelectAll(locationId)) {
      setAdminEmails((prevState) => {
        prevState[locationId] = emailsKeys;
        saveAdminEmailsNotification(prevState);
        return prevState;
      });
    } else {
      setAdminEmails((prevState) => {
        delete prevState[locationId];
        saveAdminEmailsNotification(prevState);
        return prevState;
      });
    }
  };

  const isLocationSelectAll = (locationId: number) => {
    if (!adminEmails[locationId]) {
      return false;
    }

    for (const email of emailsKeys) {
      if (!adminEmails[locationId].includes(email)) {
        return false;
      }
    }
    return true;
  };

  const saveAdminEmailsNotification = useCallback(
    debounce(async function (locationsEmails: AdminLocationEmails) {
      try {
        await updateUserAdminEmailNotifications(adminUser.adminId, locationsEmails);

        fetchAdminEmailPermissions();
        toast('Email notifications updated successfully');
      } catch (e: any) {
        toast('Something wrong updating email notifications', 'error');
      }
    }, 600),
    [],
  );

  const isLocationEmailSelected = (locationId: number, email: string) => {
    if (adminEmails[locationId]) {
      return adminEmails[locationId].includes(email);
    }

    return false;
  };

  const fetchAdminEmailPermissions = async () => {
    const res = await getUserAdminEmailNotifications(adminUser.adminId);
    setAdminEmails(res);
  };

  const init = async () => {
    await Promise.all([listAdminLocations(adminUser.adminId).then(setLocations), fetchAdminEmailPermissions()]);
  };

  useEffect(() => {
    init();
  }, []);

  const handleOnSelectLocation = (location: Location | null) => {
    if (location) {
      document.getElementById(`list-item-header-${location.id}`)?.scrollIntoView();
      setOpenGroup((prevValue) => (prevValue === location.id ? null : location.id));
    }
  };

  const selectAllEmails = () => {
    if (!isAllEmailsSelected()) {
      setAdminEmails((prevState) => {
        for (const location of locations) {
          prevState[location.id] = emailsKeys;
        }
        saveAdminEmailsNotification(prevState);
        return prevState;
      });
    } else {
      setAdminEmails({});
      saveAdminEmailsNotification({});
    }
  };

  const isAllEmailsSelected = () => {
    for (const location of locations) {
      if (!isLocationSelectAll(location.id)) {
        return false;
      }
    }

    return true;
  };

  return (
    <div className="tab-section">
      <div className="tab-title">
        <div className="w-full">
          <div className="flex w-full justify-between items-center">
            <Typography type="text-xl" weight="semi-bold">
              Email Notifications
            </Typography>
            <div className="flex items-center">
              <div className="mb-1 mr-2">
                <span className="font-sans text-sm text-black-800">Select All</span>
              </div>
              <Toggle
                onClick={() => {
                  selectAllEmails();
                }}
                checked={isAllEmailsSelected()}
              />
            </div>
          </div>

          <Typography type="text-xs" weight="regular" className="text-gray-500">
            Turn on/off admin emails by store location
          </Typography>
        </div>
      </div>

      <div className="w-full md:w-auto mb-5">
        <SelectForm<Location, false>
          isClearable
          menuShouldBlockScroll
          menuPortalTarget={document.body}
          options={locations}
          getOptionLabel={(op) => `${op.name}`}
          getOptionValue={(op) => `${op.id}`}
          filterOption={(op, inputValue) => op.data.name.toLowerCase().includes(inputValue.toLowerCase())}
          onChange={handleOnSelectLocation}
          dataCy="search_location"
          styles={{
            group: (baseStyles) => ({
              ...baseStyles,
              padding: '0',
            }),
          }}
          components={{
            Placeholder: (props) => (
              <components.Placeholder {...props}>
                <div className="flex items-center gap-[6px]">
                  <Typography type="text-sm">Search locations</Typography>
                </div>
              </components.Placeholder>
            ),
            SingleValue: (props) => (
              <components.SingleValue {...props}>
                {props.hasValue ? (
                  <Typography type="text-sm" className="text-quipli-black-800">
                    {props.getValue()[0].name}
                  </Typography>
                ) : (
                  props.children
                )}
              </components.SingleValue>
            ),
            Option: (props) => {
              const { name } = props.data;

              return (
                <div className={classNames('select-group-child')}>
                  <components.Option {...props}>
                    <div className="flex items-center justify-between">
                      <Typography type="text-sm" className="text-quipli-black-800 flex items-center gap-y-10">
                        {name}
                      </Typography>
                    </div>
                  </components.Option>
                </div>
              );
            },
          }}
        />
      </div>

      <div className="card-email">
        {locations.map((location: Location, idx: number) => (
          <div key={idx} className="list-item-email">
            <div
              id={`list-item-header-${location.id}`}
              key={idx}
              className="bg-white p-[16px] flex justify-between items-center"
            >
              <Typography type="text-md" weight="semi-bold" className="text-grey-800">
                {location.name}
              </Typography>
              <div
                onClick={() => {
                  setOpenGroup((prevValue) => (prevValue === location.id ? null : location.id));
                }}
              >
                <Icon icon="ChevronDown" />
              </div>
            </div>
            <div
              key={`content-${idx}`}
              className={classNames('hidden bg-white p-[16px] flex flex-col collapse-transition', {
                '!flex': openGroup === location.id,
              })}
            >
              <div className="flex w-full mb-2">
                <div className="mb-1 mr-2">
                  <span className="font-sans text-sm text-black-800">Select All</span>
                </div>
                <Toggle
                  onClick={() => {
                    selectAllPermissionsByLocation(location.id);
                  }}
                  checked={isLocationSelectAll(location.id)}
                />
              </div>
              {emailsGroup.map((elem: EmailGroup, index: number) => (
                <div key={idx + '-' + index} className="flex-col mb-2">
                  <Typography type="text-sm" weight="semi-bold" className="text-grey-700 mb-2">
                    {elem.module}
                  </Typography>
                  {elem.emails.map((email: EmailItem, i: number) => (
                    <div key={idx + '-' + index + '-' + i} className="flex mb-2">
                      <Checkbox
                        isChecked={isLocationEmailSelected(location.id, email.key)}
                        onChange={(isChecked) => {
                          updateEmailListByLocation(email.key, location.id, isChecked);
                        }}
                      />
                      <Typography type="text-sm" weight="regular" className="text-grey-700">
                        {email.label}
                      </Typography>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
